import { selectAll } from "@codemirror/commands";
import type { EditorView } from "@codemirror/view";
import { AudioLines, Check, ChevronRight, Columns2, ExternalLink, FileText, Minimize2, PenLine, Scan } from "lucide-react";
import { Fragment, useEffect, useLayoutEffect, useRef, useState, type MutableRefObject, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { Field, Modal, Spinner } from "../components/ui";
import { openFind } from "../editor/find";
import { insertImageFiles } from "../editor/images";
import { canReadAloud, toggleReadAloud } from "../editor/readaloud";
import {
  canRedo,
  canUndo,
  HEADINGS,
  headingAt,
  indentList,
  insertBulletList,
  insertCitation,
  insertCrossRef,
  insertDisplayMath,
  insertFigure,
  insertInlineMath,
  insertLink,
  insertNumberedList,
  insertSymbol,
  insertTable,
  outdentList,
  redo,
  setHeading,
  toggleBold,
  toggleItalic,
  undo,
} from "../editor/toolbar";
import { api, errorMessage, type Project, type TreeEntry } from "../lib/api";
import { navigate, toast, useSession } from "../lib/app";
import { ConfettiButton } from "./ConfettiButton";
import { SymbolPicker } from "./SymbolPicker";
import "./menubar.css";

export type Layout = "split" | "editor" | "pdf";
export type TreeRequest = "file" | "folder" | "upload" | "upload-folder";

interface Props {
  project: Project;
  tree: TreeEntry[];
  canEdit: boolean;
  /** The open text file's editor, if any. */
  getView: () => EditorView | null;
  /** The PDF on screen (possibly the last good one), or null. */
  pdfUrl: string | null;
  layout: Layout;
  onLayout: (layout: Layout) => void;
  focusMode: boolean;
  onFocusMode: (on: boolean) => void;
  invertPdf: boolean;
  onInvertPdf: (on: boolean) => void;
  onTreeRequest: (request: TreeRequest) => void;
  onHistory: () => void;
  onWordCount: () => void;
  onShare: () => void;
  onConnect: () => void;
  onSettings: () => void;
  onFindAll: () => void;
  onComment: () => void;
}

// ---------------------------------------------------------------- shortcuts

const MAC = /Mac|iPhone|iPad/.test(navigator.platform);
/** Overleaf's layout shortcuts: Ctrl+⌘+arrow on a Mac, Ctrl+Alt+arrow elsewhere. */
const VIEW_MOD = MAC ? "Ctrl-Mod" : "Ctrl-Alt";
const ARROWS: Record<string, string> = { ArrowUp: "↑", ArrowDown: "↓", ArrowLeft: "←", ArrowRight: "→" };

/** "Mod-Shift-f" → "⇧⌘F" on a Mac, "Ctrl+Shift+F" elsewhere. */
export function keys(spec: string): string {
  const parts = spec.split("-");
  const key = parts.pop()!;
  const has = (m: string) => parts.includes(m);
  const name = ARROWS[key] ?? (key === "Enter" ? (MAC ? "↩" : "Enter") : key.length === 1 ? key.toUpperCase() : key);
  if (MAC) return `${has("Ctrl") ? "^" : ""}${has("Alt") ? "⌥" : ""}${has("Shift") ? "⇧" : ""}${has("Mod") ? "⌘" : ""}${name}`;
  return [has("Ctrl") || has("Mod") ? "Ctrl" : "", has("Alt") ? "Alt" : "", has("Shift") ? "Shift" : "", name].filter(Boolean).join("+");
}

function matches(e: KeyboardEvent, spec: string): boolean {
  const parts = spec.split("-");
  const key = parts.pop()!;
  const has = (m: string) => parts.includes(m);
  const ctrl = has("Ctrl") || (has("Mod") && !MAC);
  const meta = has("Mod") && MAC;
  if (e.ctrlKey !== ctrl || e.metaKey !== meta || e.altKey !== has("Alt") || e.shiftKey !== has("Shift")) return false;
  // Letters by physical key, since Alt turns them into other characters on a Mac.
  return key.length === 1 ? e.code === `Key${key.toUpperCase()}` : e.key === key;
}

const LAYOUT_KEYS: Record<Layout, string> = { split: `${VIEW_MOD}-ArrowDown`, editor: `${VIEW_MOD}-ArrowLeft`, pdf: `${VIEW_MOD}-ArrowRight` };
const PDF_TAB_KEY = `${VIEW_MOD}-ArrowUp`;
const FOCUS_KEY = "Mod-Shift-m";
/** Edge's read-aloud shortcut. */
const READ_KEY = "Mod-Shift-u";

// ---------------------------------------------------------------- menu model

interface Item {
  label: string;
  icon?: ReactNode;
  hint?: string;
  checked?: boolean;
  disabled?: boolean;
  title?: string;
  onSelect?: () => void;
  /** A submenu. */
  items?: Entry[];
  /** A custom submenu body, e.g. the table size grid. */
  panel?: (keyRef: PanelKeys, keyboard: boolean) => ReactNode;
}
type Entry = Item | { heading: string } | "-";
interface Menu {
  label: string;
  /** Leave room on the left for check marks and icons on every item. */
  gutter?: boolean;
  items: Entry[];
}
/** A panel's keyboard handler; returns true if it used the key. */
type PanelKeys = MutableRefObject<((e: KeyboardEvent) => boolean) | null>;

const isItem = (e: Entry | undefined): e is Item => !!e && e !== "-" && !("heading" in e);
const hasChildren = (e: Entry | undefined): e is Item => isItem(e) && !!(e.items || e.panel);
const usable = (e: Entry | undefined): e is Item => isItem(e) && !e.disabled;

function step(entries: Entry[], from: number, dir: 1 | -1): number {
  for (let n = 1; n <= entries.length; n++) {
    const i = (((from + dir * n) % entries.length) + entries.length) % entries.length;
    if (usable(entries[i])) return i;
  }
  return from;
}
const first = (entries: Entry[]) => step(entries, -1, 1);

function download(url: string) {
  // An <a download> keeps the editor open: no "leave site?" prompt, and an error doesn't replace the page.
  const a = document.createElement("a");
  a.href = url;
  a.download = "";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const FIGURE_FILE = /\.(png|jpe?g|pdf|eps)$/i;

// ---------------------------------------------------------------- the bar

export function MenuBar(props: Props) {
  const { canEdit, getView, layout, focusMode } = props;
  const { user, canConnectClaude, updateSettings } = useSession();
  const settings = user!.settings;
  const [open, setOpen] = useState<number | null>(null);
  /** The highlighted item at each level of the open menu. */
  const [path, setPath] = useState<number[]>([]);
  /** Whether the open menu is being driven from the keyboard (shows the grid cursor). */
  const [keyboard, setKeyboard] = useState(false);
  const [anchor, setAnchor] = useState({ x: 0, y: 0 });
  const [symbols, setSymbols] = useState<HTMLElement | null>(null);
  const [dialog, setDialog] = useState<null | "copy" | "shortcuts">(null);
  const bar = useRef<HTMLDivElement>(null);
  const drop = useRef<HTMLDivElement>(null);
  const tops = useRef<Array<HTMLButtonElement | null>>([]);
  const imageInput = useRef<HTMLInputElement>(null);
  const panelKeys: PanelKeys = useRef(null);
  const hoverTimer = useRef<number | undefined>(undefined);

  const view = open !== null ? getView() : null;
  const editable = !!view && canEdit;
  /** Runs an editor command and puts the cursor back in the editor. */
  const ed = (fn: (v: EditorView) => unknown) => () => {
    const v = getView();
    if (!v) return;
    fn(v);
    v.focus();
  };

  const setLayout = (l: Layout) => {
    props.onFocusMode(false);
    props.onLayout(l);
  };
  const openPdfTab = () => {
    if (props.pdfUrl) window.open(props.pdfUrl, "_blank", "noopener");
    else toast("Compile the project first to get a PDF.");
  };

  // Global shortcuts for the View menu.
  const latest = useRef({ setLayout, openPdfTab, focusMode, onFocusMode: props.onFocusMode, getView });
  latest.current = { setLayout, openPdfTab, focusMode, onFocusMode: props.onFocusMode, getView };
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const l = (Object.keys(LAYOUT_KEYS) as Layout[]).find((k) => matches(e, LAYOUT_KEYS[k]));
      const v = latest.current.getView();
      if (l) latest.current.setLayout(l);
      else if (matches(e, PDF_TAB_KEY)) latest.current.openPdfTab();
      else if (matches(e, FOCUS_KEY)) latest.current.onFocusMode(!latest.current.focusMode);
      else if (matches(e, READ_KEY) && v && canReadAloud(v)) toggleReadAloud(v);
      else return;
      e.preventDefault();
      e.stopPropagation();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, []);

  const sel = view?.state.selection.main;
  const heading = view ? headingAt(view.state) : null;
  const images = open === 2 ? props.tree.filter((e) => e.type === "file" && FIGURE_FILE.test(e.path)).map((e) => e.path) : [];

  const menus: Menu[] = [
    {
      label: "File",
      items: [
        { label: "New file", disabled: !canEdit, onSelect: () => props.onTreeRequest("file") },
        { label: "New folder", disabled: !canEdit, onSelect: () => props.onTreeRequest("folder") },
        { label: "Upload files", disabled: !canEdit, onSelect: () => props.onTreeRequest("upload") },
        { label: "Upload folder", disabled: !canEdit, onSelect: () => props.onTreeRequest("upload-folder") },
        {
          label: "Make a copy",
          disabled: !user!.canCreateProjects,
          title: user!.canCreateProjects ? undefined : "You can't create projects until an admin verifies your account",
          onSelect: () => setDialog("copy"),
        },
        "-",
        { label: "Show version history", onSelect: props.onHistory },
        { label: "Word count", onSelect: props.onWordCount },
        "-",
        { label: "Share", onSelect: props.onShare },
        // Only for the server owner's account; nobody else sees it.
        ...(canConnectClaude
          ? [
              {
                label: "Connect Claude Code",
                disabled: !canEdit,
                title: canEdit ? "Let Claude Code put changes from your computer straight into this project" : "You need edit access for this",
                onSelect: props.onConnect,
              },
            ]
          : []),
        {
          label: "Download",
          items: [
            {
              label: "PDF",
              disabled: !props.pdfUrl,
              title: props.pdfUrl ? undefined : "Compile the project first",
              onSelect: () => props.pdfUrl && download(`${props.pdfUrl}?download=1`),
            },
            { label: "Source (.zip)", onSelect: () => download(`/api/projects/${props.project.id}/download.zip`) },
          ],
        },
        "-",
        { label: "Settings", onSelect: props.onSettings },
      ],
    },
    {
      label: "Edit",
      items: [
        { label: "Undo", hint: keys("Mod-z"), disabled: !editable || !canUndo(view.state), onSelect: ed(undo) },
        { label: "Redo", hint: keys(MAC ? "Mod-Shift-z" : "Mod-y"), disabled: !editable || !canRedo(view.state), onSelect: ed(redo) },
        "-",
        { label: "Find", hint: keys("Mod-f"), disabled: !view, onSelect: () => view && openFind(view) },
        { label: "Find and replace", hint: keys(MAC ? "Mod-Alt-f" : "Ctrl-h"), disabled: !editable, onSelect: () => view && openFind(view, { replace: true }) },
        { label: "Search all files", hint: keys("Mod-Shift-f"), onSelect: props.onFindAll },
        "-",
        { label: "Select all", hint: keys("Mod-a"), disabled: !view, onSelect: ed(selectAll) },
      ],
    },
    {
      label: "Insert",
      items: [
        {
          label: "Math",
          disabled: !editable,
          items: [
            { label: "Inline math", hint: "\\( \\)", onSelect: ed(insertInlineMath) },
            { label: "Display math", hint: "\\[ \\]", onSelect: ed(insertDisplayMath) },
          ],
        },
        {
          label: "Symbol",
          disabled: !editable,
          onSelect: () => setSymbols(tops.current[2]),
        },
        {
          label: "Figure",
          disabled: !editable,
          items: [
            { label: "Upload from computer…", onSelect: () => imageInput.current?.click() },
            {
              label: "From project files",
              items: images.length
                ? images.map((p) => ({ label: p, onSelect: ed((v) => insertFigure(v, p)) }))
                : [{ label: "No images in this project yet", disabled: true }],
            },
            "-",
            { label: "Empty figure", onSelect: ed((v) => insertFigure(v)) },
          ],
        },
        {
          label: "Table",
          disabled: !editable,
          panel: (keyRef, kb) => <TableGrid keyRef={keyRef} keyboard={kb} onPick={(r, c) => (close(), ed((v) => insertTable(v, r, c))())} />,
        },
        { label: "Citation", disabled: !editable, onSelect: ed(insertCitation) },
        { label: "Link", disabled: !editable, onSelect: ed(insertLink) },
        { label: "Cross reference", disabled: !editable, onSelect: ed(insertCrossRef) },
        "-",
        {
          label: "Comment",
          hint: keys("Mod-Alt-m"),
          disabled: !editable || !sel || sel.empty,
          title: sel?.empty ? "Select some text to comment on" : undefined,
          onSelect: props.onComment,
        },
      ],
    },
    {
      label: "View",
      gutter: true,
      items: [
        { heading: "Layout options" },
        { label: "Split view", icon: <Columns2 size={15} />, hint: keys(LAYOUT_KEYS.split), checked: layout === "split" && !focusMode, onSelect: () => setLayout("split") },
        { label: "Editor only", icon: <PenLine size={15} />, hint: keys(LAYOUT_KEYS.editor), checked: layout === "editor" && !focusMode, onSelect: () => setLayout("editor") },
        { label: "PDF only", icon: <FileText size={15} />, hint: keys(LAYOUT_KEYS.pdf), checked: layout === "pdf" && !focusMode, onSelect: () => setLayout("pdf") },
        { label: "Open PDF in separate tab", icon: <ExternalLink size={15} />, hint: keys(PDF_TAB_KEY), disabled: !props.pdfUrl, onSelect: openPdfTab },
        "-",
        { label: "Focus mode", icon: <Scan size={15} />, hint: keys(FOCUS_KEY), checked: focusMode, onSelect: () => props.onFocusMode(!focusMode) },
        {
          label: "Read aloud",
          icon: <AudioLines size={15} />,
          hint: keys(READ_KEY),
          disabled: !view || !canReadAloud(view),
          title: view && !canReadAloud(view) ? "Open a .tex, .txt or .md file to read it aloud" : "Read the file aloud from the cursor, or just the selection",
          onSelect: ed(toggleReadAloud),
        },
        "-",
        {
          label: "Editing mode",
          items: [
            { label: "Code editor", checked: settings.editorMode !== "visual", onSelect: () => updateSettings({ editorMode: "code" }) },
            { label: "Visual editor", checked: settings.editorMode === "visual", onSelect: () => updateSettings({ editorMode: "visual" }) },
          ],
        },
        "-",
        { heading: "Editor settings" },
        { label: "Wrap long lines", checked: settings.lineWrap !== false, onSelect: () => updateSettings({ lineWrap: settings.lineWrap === false }) },
        { label: "Vim key bindings", checked: settings.keymap === "vim", onSelect: () => updateSettings({ keymap: settings.keymap === "vim" ? "default" : "vim" }) },
        { label: "Auto-compile", checked: !!settings.autoCompile, onSelect: () => updateSettings({ autoCompile: !settings.autoCompile }) },
        { label: "Dark PDF", checked: props.invertPdf, onSelect: () => props.onInvertPdf(!props.invertPdf) },
      ],
    },
    {
      label: "Format",
      items: [
        { label: "Bold", hint: keys("Mod-b"), disabled: !editable, onSelect: ed(toggleBold) },
        { label: "Italics", hint: keys("Mod-i"), disabled: !editable, onSelect: ed(toggleItalic) },
        "-",
        { label: "Bullet list", disabled: !editable, onSelect: ed(insertBulletList) },
        { label: "Numbered list", disabled: !editable, onSelect: ed(insertNumberedList) },
        { label: "Increase indentation", disabled: !editable, onSelect: ed(indentList) },
        { label: "Decrease indentation", disabled: !editable, onSelect: ed(outdentList) },
        "-",
        { heading: "Paragraph styles" },
        ...HEADINGS.filter((h) => h.level !== "part").map(
          (h): Item => ({
            label: h.label,
            checked: heading === h.level,
            disabled: !editable,
            onSelect: ed((v) => setHeading(v, h.level)),
          }),
        ),
      ],
    },
    {
      label: "Help",
      items: [
        { label: "Keyboard shortcuts", onSelect: () => setDialog("shortcuts") },
        { label: "Documentation", title: "Overleaf's LaTeX guides, in a new tab", onSelect: () => window.open("https://www.overleaf.com/learn", "_blank", "noopener") },
        {
          label: "Learn LaTeX in 30 minutes",
          onSelect: () => window.open("https://www.overleaf.com/learn/latex/Learn_LaTeX_in_30_minutes", "_blank", "noopener"),
        },
      ],
    },
  ];

  /** The entries shown at `level` of the open menu. */
  const entriesAt = (level: number): Entry[] => {
    let entries = open === null ? [] : menus[open].items;
    for (let l = 0; l < level; l++) entries = (entries[path[l]] as Item | undefined)?.items ?? [];
    return entries;
  };

  function close() {
    clearTimeout(hoverTimer.current);
    setOpen(null);
    setPath([]);
  }

  function show(i: number, byKeyboard: boolean) {
    const r = tops.current[i]?.getBoundingClientRect();
    if (r) setAnchor({ x: r.left, y: r.bottom + 3 });
    setOpen(i);
    setKeyboard(byKeyboard);
    setPath(byKeyboard ? [first(menus[i].items)] : []);
  }

  function activate(item: Item, level: number, index: number) {
    if (item.disabled) return;
    if (item.items || item.panel) {
      setKeyboard(true);
      setPath([...path.slice(0, level), index, item.items ? first(item.items) : 0]);
      return;
    }
    close();
    item.onSelect?.();
  }

  // Keyboard navigation while a menu is open. Capture phase, so the editor doesn't also see the keys.
  const keyState = useRef({ path, open, menus, entriesAt, activate, show, close });
  keyState.current = { path, open, menus, entriesAt, activate, show, close };
  useEffect(() => {
    if (open === null) return;
    const onKey = (e: KeyboardEvent) => {
      const { path, open, menus, entriesAt, activate, show, close } = keyState.current;
      if (open === null) return;
      const handled = () => {
        e.preventDefault();
        e.stopPropagation();
        setKeyboard(true);
      };
      const level = Math.max(0, path.length - 1);
      const entries = entriesAt(level);
      const parent = level > 0 ? (entriesAt(level - 1)[path[level - 1]] as Item | undefined) : undefined;
      // Inside a panel (the table grid), it gets first say.
      if (parent?.panel && panelKeys.current?.(e)) return handled();
      const cur = entries[path[level]];
      switch (e.key) {
        case "ArrowDown":
        case "ArrowUp":
          if (parent?.panel) return handled();
          setPath([...path.slice(0, level), step(entries, path.length ? path[level] : e.key === "ArrowDown" ? -1 : entries.length, e.key === "ArrowDown" ? 1 : -1)]);
          return handled();
        case "Home":
        case "End":
          setPath([...path.slice(0, level), e.key === "Home" ? first(entries) : step(entries, entries.length, -1)]);
          return handled();
        case "ArrowRight":
          if (hasChildren(cur) && !cur.disabled) activate(cur, level, path[level]);
          else show((open + 1) % menus.length, true);
          return handled();
        case "ArrowLeft":
          if (path.length > 1) setPath(path.slice(0, -1));
          else show((open + menus.length - 1) % menus.length, true);
          return handled();
        case "Enter":
        case " ":
          if (usable(cur)) activate(cur, level, path[level]);
          return handled();
        case "Escape":
          if (path.length > 1) setPath(path.slice(0, -1));
          else close();
          return handled();
        case "Tab":
          close();
          return;
        default:
          // Anything else (typing, other shortcuts) closes the menu and carries on as normal.
          if (!["Shift", "Control", "Alt", "Meta"].includes(e.key)) close();
      }
    };
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!bar.current?.contains(t) && !drop.current?.contains(t)) close();
    };
    window.addEventListener("keydown", onKey, true);
    window.addEventListener("mousedown", onDown, true);
    window.addEventListener("blur", close);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("keydown", onKey, true);
      window.removeEventListener("mousedown", onDown, true);
      window.removeEventListener("blur", close);
      window.removeEventListener("resize", close);
    };
  }, [open]);

  /** Hovering an item highlights it, waiting a moment first if that would close a submenu the pointer may be heading for. */
  function hover(level: number, index: number) {
    clearTimeout(hoverTimer.current);
    const go = () => {
      setKeyboard(false);
      setPath((p) => [...p.slice(0, level), index]);
    };
    const leavingSub = path[level] !== undefined && path[level] !== index && hasChildren(entriesAt(level)[path[level]]);
    if (leavingSub) hoverTimer.current = window.setTimeout(go, 180);
    else go();
  }

  function renderLevel(entries: Entry[], level: number, gutter: boolean): ReactNode {
    return entries.map((entry, i) => {
      if (entry === "-") return <div key={i} className="mb-sep" role="separator" />;
      if (!isItem(entry)) {
        return (
          <div key={i} className="mb-heading" role="presentation">
            {entry.heading}
          </div>
        );
      }
      const active = path[level] === i;
      const sub = hasChildren(entry) && active && !entry.disabled;
      return (
        <div key={i} className="mb-row">
          <button
            type="button"
            role={entry.checked !== undefined ? "menuitemcheckbox" : "menuitem"}
            aria-haspopup={hasChildren(entry) ? "menu" : undefined}
            aria-expanded={hasChildren(entry) ? sub : undefined}
            aria-checked={entry.checked}
            className={`mb-item${active ? " active" : ""}${entry.checked ? " checked" : ""}`}
            disabled={entry.disabled}
            title={entry.title}
            tabIndex={-1}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => hover(level, i)}
            onClick={() => activate(entry, level, i)}
          >
            {(gutter || entry.icon) && <span className="mb-lead">{entry.checked ? <Check size={15} /> : entry.icon}</span>}
            <span className="mb-label">{entry.label}</span>
            {!gutter && entry.checked && <Check size={14} className="mb-tick" />}
            {entry.hint && <kbd>{entry.hint}</kbd>}
            {hasChildren(entry) && <ChevronRight size={14} className="mb-chevron" />}
          </button>
          {sub && (
            <Submenu
              onEnter={() => {
                clearTimeout(hoverTimer.current);
                setPath((p) => (p[level] === i ? p : [...p.slice(0, level), i]));
              }}
              wide={!!entry.panel}
              // Long lists (e.g. every image in a thesis) scroll; they never have submenus of their own to clip.
              scroll={!!entry.items && entry.items.length > 12}
            >
              {entry.panel
                ? entry.panel(panelKeys, keyboard && path.length > level + 1)
                : renderLevel(entry.items!, level + 1, !!entry.items!.some((e) => isItem(e) && (e.checked !== undefined || e.icon)))}
            </Submenu>
          )}
        </div>
      );
    });
  }

  return (
    <>
      <div className="menubar hide-narrow" ref={bar} role="menubar" aria-label="Editor menu">
        {menus.map((m, i) => (
          <button
            key={m.label}
            ref={(el) => {
              tops.current[i] = el;
            }}
            type="button"
            role="menuitem"
            aria-haspopup="menu"
            aria-expanded={open === i}
            className={`mb-top${open === i ? " open" : ""}`}
            onMouseDown={(e) => e.preventDefault()}
            onClick={(e) => (open === i ? close() : show(i, e.detail === 0))}
            onMouseEnter={() => open !== null && open !== i && show(i, false)}
            onKeyDown={(e) => {
              if (open !== null) return;
              if (e.key === "ArrowDown") {
                e.preventDefault();
                show(i, true);
              } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
                e.preventDefault();
                tops.current[(i + (e.key === "ArrowRight" ? 1 : menus.length - 1)) % menus.length]?.focus();
              }
            }}
          >
            {m.label}
          </button>
        ))}
        <ConfettiButton />
      </div>

      {open !== null &&
        createPortal(
          <div ref={drop} className="mb-menu" role="menu" aria-label={menus[open].label} style={{ left: anchor.x, top: anchor.y }}>
            {renderLevel(menus[open].items, 0, !!menus[open].gutter)}
          </div>,
          document.body,
        )}

      {focusMode &&
        createPortal(
          <button type="button" className="focus-exit" onClick={() => props.onFocusMode(false)}>
            <Minimize2 size={14} /> Exit focus mode <kbd>{keys(FOCUS_KEY)}</kbd>
          </button>,
          document.body,
        )}

      <input
        ref={imageInput}
        type="file"
        accept="image/*,.pdf,.eps"
        multiple
        hidden
        onChange={(e) => {
          const v = getView();
          if (v && e.target.files?.length) insertImageFiles(v, Array.from(e.target.files));
          e.target.value = "";
        }}
      />
      {symbols && (
        <SymbolPicker
          anchor={symbols}
          onPick={(latex, math) => {
            setSymbols(null);
            ed((v) => insertSymbol(v, latex, math))();
          }}
          onClose={() => setSymbols(null)}
        />
      )}
      {dialog === "copy" && <CopyDialog project={props.project} onClose={() => setDialog(null)} />}
      {dialog === "shortcuts" && <ShortcutsDialog onClose={() => setDialog(null)} />}
    </>
  );
}

/** A submenu beside its item, flipped to the left or nudged up if it would run off the screen. */
function Submenu({ children, onEnter, wide, scroll }: { children: ReactNode; onEnter: () => void; wide: boolean; scroll?: boolean }) {
  const ref = useRef<HTMLDivElement>(null);
  const [fit, setFit] = useState<{ flip: boolean; shift: number }>({ flip: false, shift: 0 });
  useLayoutEffect(() => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    setFit({ flip: r.right > innerWidth - 8, shift: Math.min(0, innerHeight - 8 - r.bottom) });
  }, []);
  return (
    <div
      ref={ref}
      className={`mb-menu mb-sub${fit.flip ? " flip" : ""}${wide ? " mb-panel" : ""}${scroll ? " mb-scroll" : ""}`}
      role="menu"
      style={{ marginTop: fit.shift }}
      onMouseEnter={onEnter}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- table size picker

const GRID_COLS = 10;
const GRID_ROWS = 8;

function TableGrid({ keyRef, keyboard, onPick }: { keyRef: PanelKeys; keyboard: boolean; onPick: (rows: number, cols: number) => void }) {
  const [at, setAt] = useState<{ r: number; c: number } | null>(keyboard ? { r: 2, c: 2 } : null);
  useEffect(() => {
    if (keyboard) setAt((a) => a ?? { r: 2, c: 2 });
  }, [keyboard]);
  const atRef = useRef(at);
  atRef.current = at;
  useEffect(() => {
    keyRef.current = (e) => {
      const a = atRef.current ?? { r: 0, c: 0 };
      const move = (r: number, c: number) => setAt({ r: Math.max(0, Math.min(GRID_ROWS - 1, r)), c: Math.max(0, Math.min(GRID_COLS - 1, c)) });
      if (e.key === "ArrowDown") move(a.r + 1, a.c);
      else if (e.key === "ArrowUp") move(a.r - 1, a.c);
      else if (e.key === "ArrowRight") move(a.r, a.c + 1);
      // Off the left edge goes back to the menu.
      else if (e.key === "ArrowLeft" && a.c > 0) move(a.r, a.c - 1);
      else if (e.key === "Enter" || e.key === " ") onPick(a.r + 1, a.c + 1);
      else return false;
      return true;
    };
    return () => {
      keyRef.current = null;
    };
  }, [keyRef, onPick]);
  return (
    <div className="table-grid" onMouseLeave={() => setAt(null)}>
      <div className="table-grid-cells" style={{ gridTemplateColumns: `repeat(${GRID_COLS}, 1fr)` }}>
        {Array.from({ length: GRID_ROWS * GRID_COLS }, (_, n) => {
          const r = Math.floor(n / GRID_COLS);
          const c = n % GRID_COLS;
          const on = !!at && r <= at.r && c <= at.c;
          return (
            <button
              key={n}
              type="button"
              tabIndex={-1}
              aria-label={`${r + 1} by ${c + 1} table`}
              className={on ? "on" : ""}
              onMouseDown={(e) => e.preventDefault()}
              onMouseEnter={() => setAt({ r, c })}
              onClick={() => onPick(r + 1, c + 1)}
            />
          );
        })}
      </div>
      <div className="table-grid-label">{at ? `${at.r + 1} × ${at.c + 1} table` : "Pick a size"}</div>
    </div>
  );
}

// ---------------------------------------------------------------- dialogs

function CopyDialog({ project, onClose }: { project: Project; onClose: () => void }) {
  const [name, setName] = useState(`${project.name} (copy)`);
  const [busy, setBusy] = useState(false);
  async function copy() {
    if (!name.trim()) return;
    setBusy(true);
    try {
      // The zip includes edits that haven't been written to disk yet.
      const res = await fetch(`/api/projects/${project.id}/download.zip`, { credentials: "same-origin" });
      if (!res.ok) throw new Error(`Couldn't read this project (${res.status}).`);
      const r = await api.upload<{ project: Project }>(`/api/projects/upload?name=${encodeURIComponent(name.trim())}`, await res.blob());
      // A zip doesn't carry the main document or compiler, so bring those across too.
      await api.patch(`/api/projects/${r.project.id}`, { rootDoc: project.rootDoc, compiler: project.compiler }).catch(() => {});
      toast(`Copied to “${r.project.name}”`, "success");
      navigate(`/project/${r.project.id}`);
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }
  return (
    <Modal
      title="Make a copy"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy || !name.trim()} onClick={copy}>
            {busy && <Spinner />} Copy project
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          copy();
        }}
      >
        <Field label="Name for the copy" hint="The copy gets all the files, but not the history, comments or people.">
          <input autoFocus value={name} onChange={(e) => setName(e.target.value)} onFocus={(e) => e.currentTarget.select()} />
        </Field>
      </form>
    </Modal>
  );
}

const SHORTCUTS: Array<[string, Array<[string, string]>]> = [
  [
    "Compiling",
    [
      ["Recompile", keys("Mod-s")],
      ["Recompile", keys("Mod-Enter")],
    ],
  ],
  [
    "Editing",
    [
      ["Undo", keys("Mod-z")],
      ["Redo", keys(MAC ? "Mod-Shift-z" : "Mod-y")],
      ["Bold", keys("Mod-b")],
      ["Italics", keys("Mod-i")],
      ["Comment out lines", keys("Mod-/")],
      ["Indent / outdent", "Tab / " + keys("Shift-Tab")],
      ["Suggest completions", keys("Ctrl-Space")],
      ["Select all", keys("Mod-a")],
    ],
  ],
  [
    "Finding",
    [
      ["Find in this file", keys("Mod-f")],
      ["Find and replace", keys(MAC ? "Mod-Alt-f" : "Ctrl-h")],
      ["Search all files", keys("Mod-Shift-f")],
      ["Search the PDF (with the PDF focused)", keys("Mod-f")],
      ["Jump from the PDF to the code", "Double-click the PDF"],
    ],
  ],
  [
    "Reviewing",
    [
      ["Comment on the selection", keys("Mod-Alt-m")],
      ["Next comment or change", keys("Mod-Alt-n")],
      ["Previous comment or change", keys("Mod-Alt-p")],
    ],
  ],
  [
    "View",
    [
      ["Split view", keys(LAYOUT_KEYS.split)],
      ["Editor only", keys(LAYOUT_KEYS.editor)],
      ["PDF only", keys(LAYOUT_KEYS.pdf)],
      ["Open PDF in separate tab", keys(PDF_TAB_KEY)],
      ["Focus mode", keys(FOCUS_KEY)],
      ["Read aloud, pause and resume", keys(READ_KEY)],
    ],
  ],
];

function ShortcutsDialog({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Keyboard shortcuts" onClose={onClose} width={620}>
      <div className="shortcut-groups">
        {SHORTCUTS.map(([group, rows]) => (
          <section key={group}>
            <h3>{group}</h3>
            <dl>
              {rows.map(([what, key], i) => (
                <Fragment key={i}>
                  <dt>{what}</dt>
                  <dd>
                    <kbd>{key}</kbd>
                  </dd>
                </Fragment>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </Modal>
  );
}
